import { GLOBAL_QUERY_KEYS } from "@/query-keys-global";
import type { ApiQueryParams } from "@/types/api-types";

import type { EntityDetailsQueryOptions } from "./types/query-types";

export const PROJECT_QUERY_KEYS = {
  HOMEPAGE_QUICK_ACTION_AUDIENCE: ["HOMEPAGE_QUICK_ACTION_AUDIENCE"],
  HOMEPAGE_QUICK_ACTION_CONFIGURATION: ["HOMEPAGE_QUICK_ACTION_CONFIGURATION"],
  ADMIN_NOTIFICATION_STATUS: ["ADMIN_NOTIFICATION"],
  ADMIN_NOTIFICATION_LIST: (query?: ApiQueryParams<"getAdminNotificationsV1">) =>
    query ? (["ADMIN_NOTIFICATION_LIST", query] as const) : (["ADMIN_NOTIFICATION_LIST"] as const),
  RESIDENT_NOTIFICATION_STATUS: ["RESIDENT_NOTIFICATION_STATUS"],
  RESIDENT_NOTIFICATION_LIST: (query?: ApiQueryParams<"getResidentNotificationsV3">) =>
    query ? (["RESIDENT_NOTIFICATION_LIST", query] as const) : (["RESIDENT_NOTIFICATION_LIST"] as const),

  AI_EMAIL_UPDATES: ["AI_EMAIL_UPDATES"],

  PROJECT_CONNECTION_INVITES: ["PROJECT_CONNECTION_INVITES"],

  ADDRESSES: ["ADDRESSES"],
  ADDRESS_LIST: (query?: { search?: string; addressTypes?: string[] }) =>
    query
      ? ([...PROJECT_QUERY_KEYS.ADDRESSES, "LIST", query] as const)
      : ([...PROJECT_QUERY_KEYS.ADDRESSES, "LIST"] as const),
  ADDRESS_LIST_INFINITE: (query?: { search?: string; addressTypes?: string[] }) =>
    query
      ? ([...PROJECT_QUERY_KEYS.ADDRESSES, "INFINITE", query] as const)
      : ([...PROJECT_QUERY_KEYS.ADDRESSES, "INFINITE"] as const),
  ADDRESS_STATS: () => [...PROJECT_QUERY_KEYS.ADDRESSES, "STATS"] as const,
  ADDRESS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.ADDRESSES, id] as const,
  ADDRESS_TYPES: () => [...PROJECT_QUERY_KEYS.ADDRESSES, "TYPES"] as const,

  ANALYTICS: ["ANALYTICS"],
  ANALYTICS_ACTIVE_CONTRIBUTORS: (dateRange: string, query?: { withCurrentMonth: boolean }) =>
    query
      ? ([...PROJECT_QUERY_KEYS.ANALYTICS, "ACTIVE_CONTRIBUTORS", dateRange, query?.withCurrentMonth || false] as const)
      : ([...PROJECT_QUERY_KEYS.ANALYTICS, "ACTIVE_CONTRIBUTORS", dateRange] as const),
  ANALYTICS_DETAILS_ADOPTION: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "DETAILS_ADOPTION", dateRange] as const,
  ANALYTICS_DETAILS_REACH: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "DETAILS_REACH", dateRange] as const,
  ANALYTICS_DETAILS_ADOPTION_VACANT: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "DETAILS_ADOPTION_VACANT", dateRange] as const,
  ANALYTICS_DETAILS_ENGAGEMENT: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "DETAILS_ENGAGEMENT", dateRange] as const,
  ANALYTICS_ENGAGED_RESIDENTS: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "ENGAGED_RESIDENTS"] as const,
  ANALYTICS_ADOPTION: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "ADOPTION"] as const,
  ANALYTICS_ADOPTION_COMPANY: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "ADOPTION_COMPANY"] as const,
  ANALYTICS_ASK_FOR_HELP_STATS: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "ASK_FOR_HELP_STATS", dateRange] as const,
  ANALYTICS_BOOKING_REPORTING: <T>(query: T) => [...PROJECT_QUERY_KEYS.ANALYTICS, "BOOKING_REPORTING", query] as const,
  ANALYTICS_COMMUNITY_FEED_REPORTING: <T>(query: T) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "COMMUNITY_FEED_REPORTING", query] as const,
  ANALYTICS_EVENT_REPORTING: <T>(query: T) => [...PROJECT_QUERY_KEYS.ANALYTICS, "EVENT_REPORTING", query] as const,
  ANALYTICS_TICKET_REPORTING: <T>(query: T) => [...PROJECT_QUERY_KEYS.ANALYTICS, "TICKET_REPORTING", query] as const,

  ANALYTICS_FEELING_AT_HOME_MONTHLY: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "FEELING_AT_HOME", dateRange] as const,
  ANALYTICS_FEELING_AT_HOME_SUGGESTIONS: () =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "FEELING_AT_HOME", "SUGGESTIONS"] as const,
  ANALYTICS_FEELING_AT_HOME: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "FEELING_AT_HOME"] as const,
  ANALYTICS_FEELING_AT_HOME_DETAILS: (query?: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.ANALYTICS, "FEELING_AT_HOME", "DETAILS", query] as const)
      : ([...PROJECT_QUERY_KEYS.ANALYTICS, "FEELING_AT_HOME", "DETAILS"] as const),
  ANALYTICS_COMMUNITY_FEED_STATS: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "COMMUNITY_FEED_STATS"] as const,

  ANALYTICS_MESSAGE_TYPES: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "MESSAGE_TYPES", dateRange] as const,
  ANALYTICS_PLATFORM_ADOPTION: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "PLATFORM_ADOPTION"] as const,
  ANALYTICS_PLATFORM_COMPANY_ADOPTION: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "PLATFORM_COMPANY_ADOPTION"] as const,
  ANALYTICS_TICKETS_AVERAGE_CLOSING_TIME: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "AVERAGE_CLOSING_TIME", dateRange] as const,
  ANALYTICS_TICKETS_AVERAGE_RATING: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "AVERAGE_RATING", dateRange] as const,
  ANALYTICS_TICKETS_BUCKETS_LEAD_TIME: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "TICKETS_BUCKETS_LEAD_TIME", dateRange] as const,
  ANALYTICS_TICKETS_CONVERSATIONS_STARTED: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "CONVERSATIONS_STARTED", dateRange] as const,
  ANALYTICS_TICKETS_OVERVIEW: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "TICKETS_OVERVIEW", dateRange] as const,
  ANALYTICS_WEEKLY_MESSAGES: () => [...PROJECT_QUERY_KEYS.ANALYTICS, "WEEKLY_MESSAGES"] as const,
  ANALYTICS_CONVERSATIONS_MONTHLY: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "CONVERSATIONS_MONTHLY", dateRange] as const,
  ANALYTICS_MESSAGE_LABEL_STATS: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "MESSAGE_LABEL_STATS", dateRange] as const,
  ANALYTICS_TICKET_LEAD_TIME_MONTHLY: (dateRange: string) =>
    [...PROJECT_QUERY_KEYS.ANALYTICS, "TICKET_LEAD_TIME_MONTHLY", dateRange] as const,

  AUTOMATED_SURVEY_QUEUE_LIST: (query?: ApiQueryParams<"getAutomatedSurveysQueuesV1">) =>
    query
      ? ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "QUEUE", "LIST", query] as const)
      : ([...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "QUEUE", "LIST"] as const),
  AUTOMATED_SURVEY_QUEUE_DETAILS: (id: string) =>
    [...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "QUEUE_DETAILS", id] as const,
  AUTOMATED_SURVEY_QUESTION_DETAILS: (queueId: string, questionId: string) =>
    [...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS, "QUEUE_DETAILS", queueId, "QUESTION_DETAILS", questionId] as const,
  AUTOMATED_SURVEY_QUESTION_RESPONSE_SUMMARY: (queueId: string, questionId: string) =>
    [
      ...GLOBAL_QUERY_KEYS.AUTOMATED_SURVEYS,
      "QUEUE_DETAILS",
      queueId,
      "QUESTION_RESPONSE_SUMMARY",
      questionId,
    ] as const,

  BOOKINGS: ["BOOKINGS"],
  BOOKINGS_ASSETS: (query?: ApiQueryParams<"getBookableAssetsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSETS", query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSETS"] as const),
  BOOKINGS_ASSETS_INFINITE: (query?: ApiQueryParams<"getBookableAssetsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSETS", "INFINITE", query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSETS", "INFINITE"] as const),
  BOOKINGS_ASSET_DETAILS: (id: string, query?: EntityDetailsQueryOptions) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_DETAILS", id, query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_DETAILS", id] as const),
  BOOKINGS_ASSET_SLOTS: (id: string, query?: ApiQueryParams<"getBookableAssetsSlotsV1", 1>) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_TIME_SLOTS", id, query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_TIME_SLOTS", id] as const),
  BOOKINGS_ASSET_STATISTICS: (id: string) => [...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_STATISTICS", id] as const,
  BOOKINGS_ASSET_BOOKINGS: (id: string, query?: ApiQueryParams<"getBookableAssetsBookingsV1", 1>) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_BOOKINGS", id, "BOOKINGS", query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "ASSET_BOOKINGS", id, "BOOKINGS"] as const),
  BOOKINGS_RESERVATION_DETAILS: (aid: string, rid: string) =>
    [...PROJECT_QUERY_KEYS.BOOKINGS, "RESERVATION_DETAILS", aid, rid] as const,
  BOOKINGS_USER_RESERVATIONS: (query?: ApiQueryParams<"getBookableAssetsMyBookingsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.BOOKINGS, "USER_RESERVATIONS", query] as const)
      : ([...PROJECT_QUERY_KEYS.BOOKINGS, "USER_RESERVATIONS"] as const),

  BUILDING_LIST: (limit?: number) => (limit ? (["BUILDINGS", limit] as const) : (["BUILDINGS"] as const)),
  BUILDING_LIST_INFINITE: () => [...PROJECT_QUERY_KEYS.BUILDING_LIST(), "INFINITE"] as const,
  BUILDING_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.BUILDING_LIST(), id] as const,

  CHATS: ["CHATS"],
  CHATS_LIST: (query?: ApiQueryParams<"getChatsV2">) =>
    query ? ([...PROJECT_QUERY_KEYS.CHATS, "LIST", query] as const) : ([...PROJECT_QUERY_KEYS.CHATS, "LIST"] as const),
  CHATS_MESSAGES: (id: string) => [...PROJECT_QUERY_KEYS.CHATS, "MESSAGES", id] as const,
  CHATS_AVAILABLE_USERS: (search: string) => [...PROJECT_QUERY_KEYS.CHATS, "AVAILABLE_USERS", search] as const,
  CHATS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.CHATS, "DETAILS", id] as const,
  CHATS_REPLIES: (id: string) => [...PROJECT_QUERY_KEYS.CHATS, "REPLIES", id] as const,
  CHATS_MEMBERS: (id: string) => [...PROJECT_QUERY_KEYS.CHATS, "MEMBERS", id] as const,

  COMPANIES: ["COMPANIES"],
  COMPANY_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.COMPANIES, "DETAILS", id],
  COMPANY_LIST_INFINITE: (query: {
    searchQuery: string;
    sortBy: string | undefined;
    sortDescending: boolean | undefined;
  }) =>
    query
      ? ([...PROJECT_QUERY_KEYS.COMPANIES, "LIST", query] as const)
      : ([...PROJECT_QUERY_KEYS.COMPANIES, "LIST"] as const),

  COMMUNITY_GROUPS: ["COMMUNITY_GROUPS"],
  COMMUNITY_GROUPS_QUERY: (query?: ApiQueryParams<"getGroupsV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, "FILTERS", query] as const)
      : ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, "FILTERS"] as const),
  COMMUNITY_GROUP_DETAILS: (id: string, query?: EntityDetailsQueryOptions) =>
    query
      ? ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, id, query] as const)
      : ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, id] as const),
  COMMUNITY_GROUP_MEMBERS: (id: string, search: string | undefined = undefined) =>
    search
      ? ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, id, "MEMBERS", search] as const)
      : ([...PROJECT_QUERY_KEYS.COMMUNITY_GROUPS, id, "MEMBERS"] as const),
  COMMUNITY_GROUPS_DEFAULT_PERMISSIONS: ["GROUPS_DEFAULT_PERMISSIONS"],

  CONSTRAINTS: ["CONSTRAINTS"],

  DASHBOARD_LINKS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.DASHBOARD_LINKS_LIST, id],
  DASHBOARD_LINKS_LIST: ["DASHBOARD_LINKS"],

  DATA_WIDGETS: ["DATA_WIDGETS"],
  DATA_WIDGETS_LIST: () => [...PROJECT_QUERY_KEYS.DATA_WIDGETS, "LIST"] as const,
  DATA_WIDGETS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.DATA_WIDGETS, "DETAILS", id] as const,

  DOCUMENTS_LIST_ALL: ["DOCUMENT_LIST"],
  DOCUMENTS_LIST: (folderId: string, query?: { Offset: number; Limit: number }) =>
    query
      ? ([...PROJECT_QUERY_KEYS.DOCUMENTS_LIST_ALL, "LIST", folderId, query] as const)
      : ([...PROJECT_QUERY_KEYS.DOCUMENTS_LIST_ALL, "LIST", folderId] as const),
  DOCUMENTS_LIST_DETAILS: (folderId: string) => [...PROJECT_QUERY_KEYS.DOCUMENTS_LIST(folderId), "DETAILS"] as const,
  DOCUMENTS_FILE_DETAILS: (folderId: string, fileId: string) =>
    [...PROJECT_QUERY_KEYS.DOCUMENTS_LIST(folderId), "FILE", fileId] as const,
  DOCUMENTS_ROOT_LIST: ["DOCUMENTS_ROOT_LIST"],
  DOCUMENTS_ROOT_FOLDER_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.DOCUMENTS_ROOT_LIST, "DETAILS", id] as const,
  DOCUMENTS_ROOT_DEFAULT_PERMISSIONS: ["DOCUMENTS_ROOT_DEFAULT_PERMISSIONS"],

  EVENT_LIST_ALL: ["EVENT_LIST"],
  EVENT_LIST: <T>(filters: T) => [...PROJECT_QUERY_KEYS.EVENT_LIST_ALL, "FILTERED", filters] as const,
  EVENT_DETAILS: (id: string, query?: ApiQueryParams<"getEventsDetailsV1", 1> & EntityDetailsQueryOptions) =>
    query
      ? ([...PROJECT_QUERY_KEYS.EVENT_LIST_ALL, "DETAILS", id, query] as const)
      : ([...PROJECT_QUERY_KEYS.EVENT_LIST_ALL, "DETAILS", id] as const),
  EVENT_PARTICIPANT_CANDIDATES_ALL: (id: string) =>
    [...PROJECT_QUERY_KEYS.EVENT_LIST_ALL, "PARTICIPANT_CANDIDATES", id] as const,
  EVENT_PARTICIPANT_CANDIDATES: <T>(id: string, query: T) =>
    [...PROJECT_QUERY_KEYS.EVENT_PARTICIPANT_CANDIDATES_ALL(id), query] as const,
  EVENT_CATEGORIES: ["EVENT_CATEGORIES"],

  FEELING_AT_HOME: ["FEELING_AT_HOME"],
  FEELING_AT_HOME_DETAILS: (query?: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME, "DETAILS", query] as const)
      : ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME, "DETAILS"] as const),
  FEELING_AT_HOME_EXPORT: (query?: ApiQueryParams<"getAnalyticsFeelingAtHomeExportV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME, "EXPORT", query] as const)
      : ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME, "EXPORT"] as const),

  FEELING_AT_HOME_HISTORY: ["FEELING_AT_HOME_HISTORY"],
  FEELING_AT_HOME_HISTORY_DETAILS: (params: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
    [...PROJECT_QUERY_KEYS.FEELING_AT_HOME_HISTORY, "DETAILS", params] as const,
  FEELING_AT_HOME_HISTORY_RESPONSES: (questionType: string) =>
    [...PROJECT_QUERY_KEYS.FEELING_AT_HOME_HISTORY, "RESPONSES", questionType] as const,
  FEELING_AT_HOME_HISTORY_QUESTION_RESPONSE_SUMMARY: (
    questionId: string,
    query?: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsQuestionsSummaryV2", 1>,
  ) =>
    query
      ? ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME_HISTORY, "QUESTION_RESPONSE_SUMMARY", questionId, query] as const)
      : ([...PROJECT_QUERY_KEYS.FEELING_AT_HOME_HISTORY, "QUESTION_RESPONSE_SUMMARY", questionId] as const),

  MESSAGES: ["MESSAGES"],
  MESSAGES_FEED_STATS: () => [...PROJECT_QUERY_KEYS.MESSAGES, "STATS"] as const,
  MESSAGES_FEED: (query?: ApiQueryParams<"getCommunityFeedV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.MESSAGES, "FEED", query] as const)
      : ([...PROJECT_QUERY_KEYS.MESSAGES, "FEED"] as const),
  MESSAGES_LIST: (query?: ApiQueryParams<"getMessagesV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.MESSAGES, "LIST", query] as const)
      : ([...PROJECT_QUERY_KEYS.MESSAGES, "LIST"] as const),
  MESSAGES_AUDIENCE: (query?: ApiQueryParams<"getCommunityFeedAudienceV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.MESSAGES, "AUDIENCE", query] as const)
      : ([...PROJECT_QUERY_KEYS.MESSAGES, "AUDIENCE"] as const),
  MESSAGE_MENTION_SUGGESTIONS: (query: ApiQueryParams<"getMessagesMentionsV1">) =>
    [...PROJECT_QUERY_KEYS.MESSAGES, "MENTION_SUGGESTIONS", query] as const,

  MESSAGE_DETAILS_ROOT: ["MESSAGES", "DETAILS"] as const,
  MESSAGE_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id] as const,
  MESSAGE_TRANSLATION: (id: string, languageCode: string) =>
    [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id, "TRANSLATIONS", languageCode] as const,
  MESSAGE_ADMINS_TO_INFORM: (id: string) =>
    [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id, "ADMINS_TO_INFORM"] as const,
  MESSAGE_INFORMED_ADMINS: (id: string) => [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id, "INFORMED_ADMINS"] as const,
  MESSAGE_REACTIONS: (id: string) => [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id, "REACTIONS"] as const,
  MESSAGE_REACH: (id: string) => [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, id, "REACH"] as const,

  MESSAGE_COMMENTS: (mid: string) => [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, "COMMENTS", mid] as const,
  MESSAGE_COMMENT_DETAILS: (mid: string, cid: string) =>
    [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, "COMMENTS", mid, cid] as const,
  MESSAGE_COMMENT_LIKES: (mid: string, cid: string) =>
    [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, "COMMENTS", mid, cid, "LIKES"] as const,
  MESSAGE_COMMENT_TRANSLATION: (mid: string, cid: string, languageCode: string) =>
    [...PROJECT_QUERY_KEYS.MESSAGE_DETAILS_ROOT, "COMMENTS", mid, cid, "TRANSLATION", languageCode] as const,

  POLLS: ["POLLS"],
  POLL_DETAILS: (pollId: string) => [...PROJECT_QUERY_KEYS.POLLS, "DETAILS", pollId] as const,
  POLL_ANSWERS: (pollId: string) => [...PROJECT_QUERY_KEYS.POLL_DETAILS(pollId), "ANSWERS"] as const,
  POLL_REACH: (pollId: string) => [...PROJECT_QUERY_KEYS.POLL_DETAILS(pollId), "REACH"] as const,
  POLL_TRANSLATION: (pollId: string, languageCode: string) =>
    [...PROJECT_QUERY_KEYS.POLL_DETAILS(pollId), "TRANSLATIONS", languageCode] as const,

  NOTIFICATION_SETTING: ["NOTIFICATION_SETTINGS"],
  NOTIFICATION_SETTINGS_USER: (userId: string) => [...PROJECT_QUERY_KEYS.NOTIFICATION_SETTING, userId] as const,

  ONBOARDING_SCREENS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.ONBOARDING_SCREENS_LIST, id],
  ONBOARDING_SCREENS_LIST: ["ONBOARDING_SCREENS"],

  PERMISSION_ROLES: ["PERMISSION_ROLES"],
  PLUS_BUTTON_OPTIONS: ["PLUS_BUTTON_OPTIONS"],
  PLUS_BUTTON_OPTIONS_LIST: () => [...PROJECT_QUERY_KEYS.PLUS_BUTTON_OPTIONS, "LIST"] as const,

  PROJECT_DETAILS: ["PROJECT_DETAILS"],
  PROJECT_SUMMARY: (params?: { from: string; to: string }) => ["PROJECT_SUMMARY", params] as const,

  INTEGRATION_REMS_CONFIGURATION: ["INTEGRATION_REMS_CONFIGURATION"],
  INTEGRATION_REMS_RECONCILIATION_DATA: (dataType: string) =>
    ["INTEGRATION_REMS_RECONCILIATION_DATA", dataType] as const,
  INTEGRATION_REMS_RECONCILIATION_RUNS: ["INTEGRATION_REMS_RECONCILIATION_RUNS"],
  INTEGRATION_ZIEZODAN_CONFIGURATION: ["INTEGRATION_ZIEZODAN_CONFIGURATION"],
  INTEGRATION_IQBI_CONFIGURATION: ["INTEGRATION_IQBI_CONFIGURATION"],
  INTEGRATION_DICO_CONFIGURATION: ["INTEGRATION_DICO_CONFIGURATION"],
  INTEGRATION_OUTLOOK_CONFIGURATION: ["INTEGRATION_OUTLOOK_CONFIGURATION"],
  INTEGRATION_JIM_CONFIGURATION: ["INTEGRATION_JIM_CONFIGURATION"],
  INTEGRATION_JIM_ROOMS: ["INTEGRATION_JIM_ROOMS"],
  INTEGRATION_MY_PUP_CONFIGURATION: ["INTEGRATION_MY_PUP_CONFIGURATION"],
  INTEGRATION_BRINGME_CONFIGURATION: ["INTEGRATION_BRINGME_CONFIGURATION"],
  INTEGRATION_HOMEDNA_CONFIGURATION: ["INTEGRATION_HOMEDNA_CONFIGURATION"],
  INTEGRATION_COPILOT_CONFIGURATION: ["INTEGRATION_COPILOT_CONFIGURATION"],
  INTEGRATION_SWOOGO_CONFIGURATION: ["INTEGRATION_SWOOGO_CONFIGURATION"],
  INTEGRATION_HEALTH_DISTRICT_CONFIGURATION: ["INTEGRATION_HEALTH_DISTRICT_CONFIGURATION"],
  INTEGRATION_OFFICE_RND_CONFIGURATION: ["INTEGRATION_OFFICE_RND_CONFIGURATION"],
  INTEGRATION_HUBSPOT_CONFIGURATION: ["INTEGRATION_HUBSPOT_CONFIGURATION"],
  INTEGRATION_WUNDERBRICKS_CONFIGURATION: ["INTEGRATION_WUNDERBRICKS_CONFIGURATION"],
  INTEGRATION_WUNDERBRICKS_TICKET_STATUS_OPTIONS: ["INTEGRATION_WUNDERBRICKS_TICKET_STATUS_OPTIONS"],
  INTEGRATION_ILOQ_CONFIGURATION: ["INTEGRATION_ILOQ_CONFIGURATION"],
  INTEGRATION_ILOQ_LOCK_GROUPS: (serviceAddress: string, customerCode: string, username: string) =>
    ["INTEGRATION_ILOQ_LOCK_GROUPS", serviceAddress, customerCode, username] as const,
  INTEGRATION_ILOQ_OPTIONS: (
    lockGroupId: string,
    serviceAddress: string,
    customerCode: string,
    username: string,
    connectionRevision: number,
  ) => ["INTEGRATION_ILOQ_OPTIONS", lockGroupId, serviceAddress, customerCode, username, connectionRevision] as const,
  INTEGRATION_JANSEN_HUYBREGTS_CONFIGURATION: ["INTEGRATION_JANSEN_HUYBREGTS_CONFIGURATION"],
  INTEGRATION_JANSEN_HUYBREGTS_TICKET_STATUS_OPTIONS: ["INTEGRATION_JANSEN_HUYBREGTS_TICKET_STATUS_OPTIONS"],

  REPAIR_CATEGORY_DEFAULT_PERMISSIONS: ["REPAIR_CATEGORY_DEFAULT_PERMISSIONS"],

  ROLE_DETAILS: (roleId: string) => [...PROJECT_QUERY_KEYS.ROLE_LIST, roleId] as const,
  ROLE_LIST: ["ROLE_LIST"],
  ROLE_TYPE_DEFAULT_PERMISSIONS: ["ROLE_TYPE_DEFAULT_PERMISSIONS"],

  SERVICE_PARTNER: (id: string, query?: EntityDetailsQueryOptions) =>
    query
      ? ([...PROJECT_QUERY_KEYS.SERVICE_PARTNERS_ALL, id, query] as const)
      : ([...PROJECT_QUERY_KEYS.SERVICE_PARTNERS_ALL, id] as const),
  SERVICE_PARTNER_IQBI: (id: string, month?: string) =>
    month
      ? ([...PROJECT_QUERY_KEYS.SERVICE_PARTNER(id), "IQBI", month] as const)
      : ([...PROJECT_QUERY_KEYS.SERVICE_PARTNER(id), "IQBI"] as const),
  SERVICE_PARTNERS_ALL: ["SERVICE_PARTNERS_LIST"],
  SERVICE_PARTNERS_CREATE_TYPES: ["SERVICE_PARTNERS_CREATE_TYPES"],
  SERVICE_PARTNERS_ALL_INFINITE: () => [...PROJECT_QUERY_KEYS.SERVICE_PARTNERS_ALL, "INFINITE"] as const,
  SERVICE_PARTNERS_CATEGORIES: ["SERVICE_PARTNERS_CATEGORIES"],
  SERVICE_PARTNERS_DEFAULT_PERMISSIONS: ["SERVICE_PARTNERS_DEFAULT_PERMISSIONS"],

  SURVEYS: ["SURVEYS"],
  SURVEYS_QUERY: (query?: ApiQueryParams<"getSurveysV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.SURVEYS, "LIST", query] as const)
      : ([...PROJECT_QUERY_KEYS.SURVEYS, "LIST"] as const),
  SURVEY_DETAILS: (id: string, options?: EntityDetailsQueryOptions) =>
    [...PROJECT_QUERY_KEYS.SURVEYS, "DETAILS", id, options] as const,
  SURVEY_DETAILS_RESPONSE_SUMMARY: (surveyId: string, questionId: string) =>
    [...PROJECT_QUERY_KEYS.SURVEYS, surveyId, "QUESTION_RESPONSE_SUMMARY", questionId] as const,
  SURVEY_EXPORT: (id: string, query?: ApiQueryParams<"getSurveysExportV2", 1>) =>
    query
      ? ([...PROJECT_QUERY_KEYS.SURVEYS, "DETAILS", id, "EXPORT", query] as const)
      : ([...PROJECT_QUERY_KEYS.SURVEYS, "DETAILS", id, "EXPORT"] as const),
  SURVEY_SUGGESTED_FOLLOW_UPS: (surveyId: string) =>
    [...PROJECT_QUERY_KEYS.SURVEYS, "SUGGESTED_FOLLOW_UPS", surveyId] as const,

  TICKET_ASSIGNEES: ["TICKET_ASSIGNEES"],

  TICKET_CATEGORIES: ["TICKET_CATEGORIES"],
  TICKET_CATEGORIES_RESIDENT: () => [...PROJECT_QUERY_KEYS.TICKET_CATEGORIES, "RESIDENT"] as const,
  TICKET_CATEGORY: (id: string) => [...PROJECT_QUERY_KEYS.TICKET_CATEGORIES, id] as const,
  TICKET_STATUSES: ["TICKET_STATUSES"],
  TICKET_STATUS: (id: string) => [...PROJECT_QUERY_KEYS.TICKET_STATUSES, id] as const,

  TICKET_CATEGORY_TREE: (categoryId: string) => ["TICKET_CATEGORY_TREE", categoryId] as const,
  TICKET_CATEGORY_CREATABLES: ["TICKET_CATEGORY_CREATABLES"],
  TICKET_CATEGORY_CREATABLES_LIST: (query?: ApiQueryParams<"getTicketCategoriesCreatableV2">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKET_CATEGORY_CREATABLES, query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKET_CATEGORY_CREATABLES] as const),
  TICKET_CATEGORY_CREATABLE_DETAILS: (
    id: string,
    query?: ApiQueryParams<"getTicketCategoriesCreatableDetailsV2", 1>,
  ) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKET_CATEGORY_CREATABLES, id, query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKET_CATEGORY_CREATABLES, id] as const),

  TICKETS: ["TICKETS"],
  TICKETS_FEED: (query?: ApiQueryParams<"getAdminTicketsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS, "FEED", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS, "FEED"] as const),
  TICKETS_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.TICKETS, "DETAILS", id] as const,
  TICKETS_DENIAL_REPLY_SUGGESTION: (id: string) =>
    [...PROJECT_QUERY_KEYS.TICKETS, "REJECT_DENIAL_REPLY_SUGGESTION", id] as const,
  TICKETS_RESPONSIBILITY_SUGGESTION: (id: string) =>
    [...PROJECT_QUERY_KEYS.TICKETS_DETAILS(id), "RESPONSIBILITY_SUGGESTION"] as const,
  TICKETS_DETAILS_TRANSLATION: (id: string, languageId: string) =>
    [...PROJECT_QUERY_KEYS.TICKETS_DETAILS(id), "TRANSLATION", languageId] as const,
  TICKETS_DETAILS_APPOINTMENT: (id: string) => [...PROJECT_QUERY_KEYS.TICKETS_DETAILS(id), "APPOINTMENT"] as const,
  TICKETS_APPOINTMENT_PLANNING: (planningId: string) =>
    [...PROJECT_QUERY_KEYS.TICKETS, "APPOINTMENT_PLANNING", planningId] as const,
  TICKETS_DETAILS_ACTIVITIES: (id: string, query?: ApiQueryParams<"getAdminTicketsActivitiesV1", 1>) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS_DETAILS(id), "ACTIVITIES", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS_DETAILS(id), "ACTIVITIES"] as const),
  TICKET_COMMENT_TRANSLATION: (ticketId: string, commentId: string, languageCode: string) =>
    [
      ...PROJECT_QUERY_KEYS.TICKETS_DETAILS(ticketId),
      "ACTIVITES",
      "COMMENT_TRANSLATION",
      commentId,
      languageCode,
    ] as const,
  TICKET_RATING_FEEDBACK_OPTIONS: () => [...PROJECT_QUERY_KEYS.TICKETS, "RATING_FEEDBACK_OPTIONS"] as const,
  TICKETS_FILTERS_VALUES: () => [...PROJECT_QUERY_KEYS.TICKETS, "FILTERS_VALUES"] as const,
  TICKETS_HISTORY: (id: string, type: string) => [...PROJECT_QUERY_KEYS.TICKETS, "HISTORY", id, type] as const,
  TICKETS_RESIDENT_DETAILS: (id: string) => [...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_DETAILS", id] as const,
  TICKETS_RESIDENT_DETAILS_COMMENTS: (id: string, query?: ApiQueryParams<"getTicketsCommentsV1", 1>) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_DETAILS", id, "COMMENTS", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_DETAILS", id, "COMMENTS"] as const),
  TICKETS_RESIDENT_FEED: (query?: ApiQueryParams<"getTicketsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_FEED", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_FEED"] as const),
  TICKETS_RESIDENT_FILTERS_VALUES: () => [...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_FILTERS_VALUES"] as const,
  TICKETS_RESIDENT_TAB_TOTAL: (tab: string) => [...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_TAB_TOTAL", tab] as const,
  TICKETS_RESIDENT_TABS_AMOUNT: (query?: ApiQueryParams<"getTicketsStatsAllStatusesV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_TABS_AMOUNT", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS, "RESIDENT_TABS_AMOUNT"] as const),
  TICKETS_TAB_TOTAL: (tab: string) => [...PROJECT_QUERY_KEYS.TICKETS, "TAB_TOTAL", tab] as const,
  TICKETS_TABS_AMOUNT: (query?: ApiQueryParams<"getAdminTicketsStatsAllTabsV1">) =>
    query
      ? ([...PROJECT_QUERY_KEYS.TICKETS, "TABS_AMOUNT", query] as const)
      : ([...PROJECT_QUERY_KEYS.TICKETS, "TABS_AMOUNT"] as const),

  USERS: ["USERS"],
  USERS_ADMINS: ["USERS_ADMINS"],
  USERS_QUERY: (query?: ApiQueryParams<"getUsersV1">) =>
    query ? (["USERS", "FILTERS", query] as const) : (["USERS", "FILTERS"] as const),
  USERS_EXPORT: (query?: ApiQueryParams<"getUsersV1">) =>
    query ? (["USERS", "EXPORT", query] as const) : (["USERS", "EXPORT"] as const),
  USER_DETAILS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, userId] as const,
  USER_CHAT_DETAILS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "CHAT_DETAILS", userId] as const,
  USER_POSTS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "POSTS", userId] as const,
  USER_PROJECTS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "PROJECTS", userId] as const,
  USER_ROLE: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "ROLE", userId] as const,
  USER_SIGNUP_REMINDERS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "SIGNUP_REMINDERS", userId] as const,
  USER_DELETABLE_PROJECTS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "DELETABLE_PROJECTS", userId] as const,
  USER_ILOQ_STATUS: (userId: string) => [...PROJECT_QUERY_KEYS.USERS, "ILOQ_STATUS", userId] as const,
} as const;
